import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSecurityCves, getSecurityCveDetail } from '../api/internal/usersInternalApi';
import {
    Card, PageHead, Button, IconButton, Pill, Input, Select,
    EmptyState, Spinner, SpinnerContainer, Notice, DataTable, TableScroll,
    FieldLabel,
} from '../components/ui';
import {
    FilterBar, ClickableRow, MonoCell, Muted, Pager,
} from '../components/security/security.styled';
import { SeverityPill, SEVERITY_ORDER, formatEpss, formatDate } from '../components/security/severity';
import CveDetailView from '../components/security/cveDetailView';

const PAGE_SIZE = 50;

function CveDetail({ cveCode, onBack }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        getSecurityCveDetail(cveCode, controller.signal)
            .then((data) => { if (data) setDetail(data); })
            .catch((e) => setError(e.response?.data?.error || 'Chargement impossible.'))
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [cveCode]);

    return (
        <>
            <PageHead>
                <div>
                    <h2><MonoCell style={{ fontSize: '1.3rem' }}>{cveCode}</MonoCell></h2>
                    <p>Appareils du parc exposés à cette vulnérabilité.</p>
                </div>
                <Button $variant="secondary" onClick={onBack}>
                    <i className="bx bx-arrow-back" />
                    Retour à la liste
                </Button>
            </PageHead>

            {loading ? (
                <Card><SpinnerContainer><Spinner /></SpinnerContainer></Card>
            ) : error ? (
                <Card>
                    <Notice $tone="danger"><i className="bx bx-error-circle" />{error}</Notice>
                </Card>
            ) : (
                <CveDetailView detail={detail} />
            )}
        </>
    );
}

export default function SecurityCves() {
    // La CVE sélectionnée vit dans l'URL : un lien depuis la vue d'ensemble ou un
    // partage de lien ouvre directement la bonne fiche.
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedCve = searchParams.get('cve');

    const [search, setSearch] = useState('');
    const [severity, setSeverity] = useState('');
    const [exploitOnly, setExploitOnly] = useState(false);
    const [sortField, setSortField] = useState('epssScore');
    const [page, setPage] = useState(1);

    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPage = useCallback((signal) => {
        setLoading(true);
        setError(null);
        return getSecurityCves(
            { page, limit: PAGE_SIZE, search, severity, exploitOnly: exploitOnly || '', sortField },
            signal
        )
            .then((data) => { if (data) setResult(data); })
            .catch(() => setError('Chargement des vulnérabilités impossible.'))
            .finally(() => setLoading(false));
    }, [page, search, severity, exploitOnly, sortField]);

    // Debounce sur la saisie : évite une requête par frappe
    useEffect(() => {
        if (selectedCve) return undefined;
        const controller = new AbortController();
        const timer = setTimeout(() => fetchPage(controller.signal), search ? 300 : 0);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [fetchPage, selectedCve, search]);

    // Tout changement de filtre ramène à la première page
    const updateFilter = (setter) => (value) => { setter(value); setPage(1); };

    if (selectedCve) {
        return (
            <CveDetail
                cveCode={selectedCve}
                onBack={() => setSearchParams({}, { replace: true })}
            />
        );
    }

    const pagination = result?.pagination;

    return (
        <>
            <PageHead>
                <div>
                    <h2>Vulnérabilités</h2>
                    <p>Toutes les CVE recensées sur le parc, triées par priorité de traitement.</p>
                </div>
            </PageHead>

            <Card>
                <FilterBar>
                    <div style={{ flex: 1, minWidth: '220px' }}>
                        <FieldLabel htmlFor="cve-search">Recherche</FieldLabel>
                        <Input
                            id="cve-search"
                            style={{ width: '100%', marginTop: '6px' }}
                            placeholder="CVE-2021-44228 ou nom de logiciel..."
                            value={search}
                            onChange={(e) => updateFilter(setSearch)(e.target.value)}
                        />
                    </div>
                    <div>
                        <FieldLabel htmlFor="cve-severity">Sévérité</FieldLabel>
                        <Select
                            id="cve-severity"
                            style={{ marginTop: '6px' }}
                            value={severity}
                            onChange={(e) => updateFilter(setSeverity)(e.target.value)}
                        >
                            <option value="">Toutes</option>
                            {SEVERITY_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Select>
                    </div>
                    <div>
                        <FieldLabel htmlFor="cve-sort">Trier par</FieldLabel>
                        <Select
                            id="cve-sort"
                            style={{ marginTop: '6px' }}
                            value={sortField}
                            onChange={(e) => updateFilter(setSortField)(e.target.value)}
                        >
                            <option value="epssScore">Probabilité d'exploitation</option>
                            <option value="affectedCount">Appareils touchés</option>
                            <option value="cvssScore">Score CVSS</option>
                            <option value="publishedOn">Date de publication</option>
                        </Select>
                    </div>
                    <label htmlFor="cve-exploit">
                        <input
                            id="cve-exploit"
                            type="checkbox"
                            checked={exploitOnly}
                            onChange={(e) => updateFilter(setExploitOnly)(e.target.checked)}
                        />
                        Exploit connu uniquement
                    </label>
                </FilterBar>
            </Card>

            <Card $pad="16px">
                {error ? (
                    <Notice $tone="danger"><i className="bx bx-error-circle" />{error}</Notice>
                ) : loading && !result ? (
                    <SpinnerContainer><Spinner /></SpinnerContainer>
                ) : result?.data.length === 0 ? (
                    <EmptyState>
                        <i className="bx bx-search-alt-2" />
                        <h3>Aucune vulnérabilité</h3>
                        <p>Aucune CVE ne correspond à ces critères.</p>
                    </EmptyState>
                ) : (
                    <>
                        <TableScroll>
                            <DataTable>
                                <thead>
                                    <tr>
                                        <th>CVE</th>
                                        <th>Sévérité</th>
                                        <th>CVSS</th>
                                        <th>EPSS</th>
                                        <th>Exploit</th>
                                        <th>Appareils</th>
                                        <th>Correctif</th>
                                        <th>Logiciel</th>
                                        <th>Publiée</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.data.map((cve) => (
                                        <ClickableRow
                                            key={cve.cveCode}
                                            onClick={() => setSearchParams({ cve: cve.cveCode })}
                                        >
                                            <td><MonoCell>{cve.cveCode}</MonoCell></td>
                                            <td><SeverityPill severity={cve.severity} /></td>
                                            <td>{cve.cvssScore ?? '—'}</td>
                                            <td>{formatEpss(cve.epssScore)}</td>
                                            <td>
                                                {cve.exploitabilityLevel === 'PoC'
                                                    ? <Pill $tone="danger">Oui</Pill>
                                                    : <Muted>—</Muted>}
                                            </td>
                                            <td>{cve.affectedCount}</td>
                                            <td>
                                                {cve.securityUpdateAvailable
                                                    ? <Pill $tone="ok">Dispo.</Pill>
                                                    : <Muted>—</Muted>}
                                            </td>
                                            <td><Muted>{(cve.software || []).slice(0, 2).join(', ') || '—'}</Muted></td>
                                            <td><Muted>{formatDate(cve.publishedOn)}</Muted></td>
                                        </ClickableRow>
                                    ))}
                                </tbody>
                            </DataTable>
                        </TableScroll>

                        {pagination && (
                            <Pager>
                                <span>
                                    <strong>{pagination.totalCount.toLocaleString('fr-FR')}</strong> vulnérabilités
                                </span>
                                <div className="spacer" />
                                <span>Page <strong>{pagination.page}</strong> / {pagination.totalPages}</span>
                                <IconButton
                                    $bordered
                                    disabled={!pagination.hasPrevPage || loading}
                                    onClick={() => setPage((p) => p - 1)}
                                    title="Page précédente"
                                >
                                    <i className="bx bx-chevron-left" />
                                </IconButton>
                                <IconButton
                                    $bordered
                                    disabled={!pagination.hasNextPage || loading}
                                    onClick={() => setPage((p) => p + 1)}
                                    title="Page suivante"
                                >
                                    <i className="bx bx-chevron-right" />
                                </IconButton>
                            </Pager>
                        )}
                    </>
                )}
            </Card>
        </>
    );
}
